import type { HsNodeSmallView, HsNodesVisibilityView } from '@sibe/hyperscene';

export type HsVisibilityState = 'visible' | 'hidden' | 'mixed' | 'unknown';

/** A scene tree node as a host shows it; children load on demand. */
export interface HsSceneTreeNode {
  id: string;
  title: string;
  visibility: HsVisibilityState;
  hasChildren: boolean;
  level: number;
  allChildrenCount: number;
  parentNodeId: string | null;
  tags: string[];
  childrenLoaded: boolean;
  childrenLoading: boolean;
  isExpanded: boolean;
  children: HsSceneTreeNode[];
}

/** The node endpoints the tree reads; `HsViewerApi` provides them. */
export interface HsSceneTreeSource {
  getSceneRootNodes(sceneId: string): Promise<HsNodeSmallView[]>;
  getNodeChildren(nodeId: string): Promise<HsNodeSmallView[]>;
  getNodeChildrenBulk(nodeIds: readonly string[]): Promise<HsNodeSmallView[]>;
  getNodeRoute(nodeId: string): Promise<string[]>;
  getNodesVisibility(sceneId: string, nodeIds: readonly string[]): Promise<HsNodesVisibilityView>;
}

export function hsMakeSceneTreeNode(node: HsNodeSmallView): HsSceneTreeNode {
  return {
    id: node.id,
    title: node.title,
    visibility: 'unknown',
    hasChildren: node.hasChildren,
    level: node.level,
    allChildrenCount: node.allChildrenCount,
    parentNodeId: node.parentNodeId,
    tags: node.tags,
    childrenLoaded: false,
    childrenLoading: false,
    isExpanded: false,
    children: [],
  };
}

/**
 * The lazily loaded node tree of a scene with per-node visibility. Nodes are plain objects the host can
 * bind to directly; the tree mutates them in place.
 */
export class HsSceneTree {
  public roots: HsSceneTreeNode[] = [];

  public constructor(private readonly source: HsSceneTreeSource) {}

  /** Loads the scene's roots (optionally filtered) with their visibility; returns the raw roots it kept. */
  public async load(
    sceneId: string,
    filter: (node: HsNodeSmallView) => boolean = () => true
  ): Promise<HsNodeSmallView[]> {
    const roots: HsNodeSmallView[] = (await this.source.getSceneRootNodes(sceneId)).filter(filter);
    this.roots = roots.map(hsMakeSceneTreeNode);
    await this.refreshVisibility(sceneId, this.roots);
    return roots;
  }

  /** Loads a node's children once; `childrenLoading` is set while the request runs. */
  public async loadChildren(sceneId: string | null, node: HsSceneTreeNode): Promise<void> {
    if (node.childrenLoaded) {
      return;
    }
    node.childrenLoading = true;
    try {
      const children: HsNodeSmallView[] = await this.source.getNodeChildren(node.id);
      node.children = children.map(hsMakeSceneTreeNode);
      await this.refreshVisibility(sceneId, node.children);
      node.childrenLoaded = true;
    } finally {
      node.childrenLoading = false;
    }
  }

  /**
   * Reads the visibility of the nodes from the API; a node that is partly hidden becomes `'mixed'`.
   * Without a scene id it leaves the nodes as they are.
   */
  public async refreshVisibility(sceneId: string | null, nodes: readonly HsSceneTreeNode[]): Promise<void> {
    if (sceneId === null || nodes.length === 0) {
      return;
    }
    const state: HsNodesVisibilityView = await this.source.getNodesVisibility(
      sceneId,
      nodes.map((node: HsSceneTreeNode) => node.id)
    );
    const enabled: Set<string> = new Set(state.enabled);
    const disabled: Set<string> = new Set(state.disabled);
    for (const node of nodes) {
      const isEnabled: boolean = enabled.has(node.id);
      const isDisabled: boolean = disabled.has(node.id);
      node.visibility = isEnabled && isDisabled ? 'mixed' : isEnabled ? 'visible' : isDisabled ? 'hidden' : 'unknown';
    }
  }

  public find(nodeId: string, nodes: readonly HsSceneTreeNode[] = this.roots): HsSceneTreeNode | null {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return node;
      }
      const nested: HsSceneTreeNode | null = this.find(nodeId, node.children);
      if (nested !== null) {
        return nested;
      }
    }
    return null;
  }

  public setVisibility(nodeId: string, visibility: HsVisibilityState): void {
    const node: HsSceneTreeNode | null = this.find(nodeId);
    if (node !== null) {
      node.visibility = visibility;
    }
  }

  /** Sets the visibility of a node and its loaded descendants. */
  public setSubtreeVisibility(nodeId: string, visibility: HsVisibilityState): void {
    const node: HsSceneTreeNode | null = this.find(nodeId);
    if (node !== null) {
      node.visibility = visibility;
      this.setLoadedVisibility(node.children, visibility);
    }
  }

  /** Sets the visibility of the nodes and all their loaded descendants. */
  public setLoadedVisibility(nodes: readonly HsSceneTreeNode[], visibility: HsVisibilityState): void {
    for (const node of nodes) {
      node.visibility = visibility;
      this.setLoadedVisibility(node.children, visibility);
    }
  }

  /**
   * Loads and expands the ancestors of a node so a host can show it; returns the node, or null when it
   * is not under the loaded roots. Children loaded this way have unknown visibility.
   */
  public async reveal(nodeId: string): Promise<HsSceneTreeNode | null> {
    const parentNodeIds: string[] = await this.source.getNodeRoute(nodeId);
    const path: string[] = [...parentNodeIds, nodeId].filter(
      (pathNodeId: string, index: number, pathNodeIds: string[]) =>
        pathNodeId.length > 0 && pathNodeIds.indexOf(pathNodeId) === index
    );
    const childNodes: HsNodeSmallView[] =
      parentNodeIds.length > 0 ? await this.source.getNodeChildrenBulk(parentNodeIds) : [];
    const childrenByParentId: Map<string, HsNodeSmallView[]> = new Map();
    for (const childNode of childNodes) {
      if (childNode.parentNodeId === null) {
        continue;
      }
      const children: HsNodeSmallView[] = childrenByParentId.get(childNode.parentNodeId) ?? [];
      children.push(childNode);
      childrenByParentId.set(childNode.parentNodeId, children);
    }
    let treeNode: HsSceneTreeNode | null = this.find(path[0] ?? nodeId);
    for (let index: number = 0; treeNode !== null && index < path.length - 1; index++) {
      if (!treeNode.childrenLoaded) {
        treeNode.children = (childrenByParentId.get(treeNode.id) ?? []).map(hsMakeSceneTreeNode);
        treeNode.childrenLoaded = true;
      }
      treeNode.isExpanded = true;
      treeNode = treeNode.children.find((child: HsSceneTreeNode) => child.id === path[index + 1]) ?? null;
    }
    return treeNode;
  }
}
